/**
 * 绘制 / 编辑形状状态机 hook。
 *
 * <ul>
 *   <li>draw: 画新面, 双击闭合后进入"待创建"(draftGeometry 非空, 抽屉打开填属性, 未落库);</li>
 *   <li>edit: 编辑选中面形状, 改动记在 editedGeometry (pending), 抽屉点保存才提交;</li>
 *   <li>idle: 无交互。</li>
 * </ul>
 */

import { useCallback, useState } from 'react';
import { DrawMode, GeoJsonGeometry } from '../types';

export interface DrawController {
    mode: DrawMode;
    /** 新画好、待创建的几何 (非空 = 抽屉处于"新建 AOI"模式)。 */
    draftGeometry: GeoJsonGeometry | null;
    /** 编辑形状后的 pending 几何 (非空 = 有未保存的形状改动)。 */
    editedGeometry: GeoJsonGeometry | null;
    startDraw: () => void;
    cancelDraw: () => void;
    /** 双击闭合完成绘制。 */
    completeDraw: (geometry: GeoJsonGeometry) => void;
    /** 清掉待创建草稿 (创建成功 / 用户关闭新建抽屉 / 放弃草稿)。 */
    clearDraft: () => void;
    /** 草稿态下改形状: 顶点编辑结果直接写回草稿 (草稿本身还没落库, 没有"pending 改动"概念)。 */
    updateDraft: (geometry: GeoJsonGeometry) => void;
    startEdit: () => void;
    /** 结束编辑 (改动保留在 editedGeometry, 等保存)。 */
    stopEdit: () => void;
    /** 编辑过程中每次形变回调。 */
    onEditChange: (geometry: GeoJsonGeometry) => void;
    /** 保存成功 / 放弃改动后清 pending 几何。 */
    clearEdited: () => void;
}

export function useDrawController(): DrawController {
    const [mode, setMode] = useState<DrawMode>('idle');
    const [draftGeometry, setDraftGeometry] = useState<GeoJsonGeometry | null>(null);
    const [editedGeometry, setEditedGeometry] = useState<GeoJsonGeometry | null>(null);

    const startDraw = useCallback(() => {
        setDraftGeometry(null);
        setEditedGeometry(null);
        setMode('draw');
    }, []);

    const cancelDraw = useCallback(() => {
        setMode((m) => (m === 'draw' ? 'idle' : m));
    }, []);

    const completeDraw = useCallback((geometry: GeoJsonGeometry) => {
        setDraftGeometry(geometry);
        setMode('idle');
    }, []);

    const clearDraft = useCallback(() => {
        setDraftGeometry(null);
        setMode((m) => (m === 'edit' ? 'idle' : m));
    }, []);

    const updateDraft = useCallback((geometry: GeoJsonGeometry) => {
        setDraftGeometry(geometry);
    }, []);

    const startEdit = useCallback(() => {
        setMode('edit');
    }, []);

    const stopEdit = useCallback(() => {
        setMode((m) => (m === 'edit' ? 'idle' : m));
    }, []);

    const onEditChange = useCallback((geometry: GeoJsonGeometry) => {
        setEditedGeometry(geometry);
    }, []);

    const clearEdited = useCallback(() => {
        setEditedGeometry(null);
    }, []);

    return {
        mode,
        draftGeometry,
        editedGeometry,
        startDraw,
        cancelDraw,
        completeDraw,
        clearDraft,
        updateDraft,
        startEdit,
        stopEdit,
        onEditChange,
        clearEdited,
    };
}
